import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, BookOpen, Quote, Languages, Church } from 'lucide-react';
import { fetchTheme } from '@/services/api';

interface YearThemeData {
    year: string;
    theme: string;
    bible_reference: string;
    translation_en: string;
    translation_hebrew?: string;
    translation_greek?: string;
    translation_yoruba?: string;
    watchword: string;
}

const YearTheme = () => {
    const [theme, setTheme] = useState<YearThemeData | null>(null);
    const [loading, setLoading] = useState(true);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const loadTheme = async () => {
            setLoading(true);
            try {
                const res = await fetchTheme(currentYear);
                setTheme(res.data.data);
            } catch (err) {
                console.error('Failed to fetch theme info:', err);
            } finally {
                setLoading(false);
            }
        };
        loadTheme();
    }, [currentYear]);

    if (loading || !theme) {
        return (
            <div className="flex items-center justify-center py-24 bg-slate-50">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    <p className="font-serif italic text-slate-500 tracking-widest text-sm">Revealing the {currentYear} Theme...</p>
                </div>
            </div>
        );
    }

    return (
        <section className="relative py-24 px-4 overflow-hidden bg-white">
            {/* Background Architectural Flourish */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 opacity-[0.03] pointer-events-none">
                <Church size={600} strokeWidth={0.5} />
            </div>

            <div className="max-w-5xl mx-auto relative z-10">
                <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center space-y-10"
                >
                    {/* Header Badge */}
                    <div className="flex flex-col items-center gap-4">
                        <div className="flex items-center gap-4 text-amber-600">
                            <div className="h-px w-12 bg-amber-500/30" />
                            <Sparkles size={20} className="animate-pulse" />
                            <div className="h-px w-12 bg-amber-500/30" />
                        </div>
                        <h2 className="text-[11px] font-bold uppercase tracking-[0.5em] text-slate-500">
                            The {theme.year} Diocesan Theme
                        </h2>
                    </div>

                    {/* Main Theme Display */}
                    <div className="space-y-6">
                        {(theme.translation_hebrew || theme.translation_greek) && (
                            <motion.h1 
                                initial={{ scale: 0.95 }}
                                whileInView={{ scale: 1 }}
                                className="text-6xl md:text-8xl font-serif font-bold text-slate-900 tracking-tight"
                            >
                                {theme.translation_hebrew || theme.translation_greek}
                            </motion.h1>
                        )}
                        
                        <div className="relative inline-block">
                            <span className="text-2xl md:text-4xl font-serif italic text-amber-600 block mb-2">
                                “{theme.translation_en}”
                            </span>
                            <div className="h-1 w-24 bg-amber-500/20 mx-auto rounded-full" />
                        </div>

                        {theme.translation_yoruba && (
                            <p className="text-lg md:text-xl font-serif text-slate-400 italic">
                                Yoruba: <span className="text-slate-600">“{theme.translation_yoruba}”</span>
                            </p>
                        )}
                    </div>

                    {/* Scholarly Content Grid */}
                    <div className="grid md:grid-cols-2 gap-8 mt-16 pt-16 border-t border-slate-100">
                        {/* Scripture Card */}
                        <div className="bg-slate-50 rounded-[2rem] p-8 text-left group hover:bg-amber-50 transition-colors duration-500">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-white rounded-xl shadow-sm group-hover:text-amber-600 transition-colors">
                                    <BookOpen size={20} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Foundation Scripture</span>
                            </div>
                            <h3 className="text-2xl font-serif font-bold text-slate-900 mb-2">
                                {theme.bible_reference}
                            </h3>
                            <p className="text-slate-500 text-sm leading-relaxed italic">
                                Rooting our spiritual journey in the unfailing Word of God for this season.
                            </p>
                        </div>

                        {/* Watchword Card */}
                        <div className="bg-slate-900 rounded-[2rem] p-8 text-left relative overflow-hidden group">
                            <Quote className="absolute -right-4 -bottom-4 w-32 h-32 text-white/5 rotate-12" />
                            <div className="flex items-center gap-4 mb-4">
                                <div className="p-3 bg-white/10 rounded-xl text-amber-500">
                                    <Languages size={20} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Our Watchword</span>
                            </div>
                            <p className="text-lg font-serif italic text-slate-200 relative z-10 leading-relaxed">
                                "{theme.watchword}"
                            </p>
                        </div>
                    </div>

                    {/* Concept Definition Tag */}
                    <div className="pt-8">
                        <span className="px-6 py-2 bg-slate-100 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 border border-slate-200">
                            Theme Concept: {theme.theme}
                        </span>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default YearTheme;